import { IAccount, IImpersonationContext } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { parse as getQueryStringParams, stringify } from 'query-string';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import { ILocationState } from '../../../../models';
import { IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import {
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../viewmodels/AdminViewModels';
import { Input } from '../../../../web/components-v2/Input';
import { ExternalIntegrations } from '../../../../web/components/integrations/ExternalIntegrations';
import { AdminSocialMediaReportingTable } from '../../../../web/components/socialMedia/SocialMediaReportingTable/AdminSocialReportingTable';
import { TagsContainer } from '../../../../web/containers/PeopleV2/TagsContainer';
import { AccountAutomationsTab } from '../../../containers/account/accountTabs/AccountAutomationsTab';
import { AdminIntegrations } from '../../../containers/account/accountTabs/AdminIntegrations';
import { AdminLeadParsers } from '../../../containers/account/accountTabs/AdminLeadParsers';
import { GroupEmails } from '../../../containers/account/accountTabs/GroupEmails';
import { Imports } from '../../../containers/account/accountTabs/Imports';
import { Settings } from '../../../containers/account/accountTabs/Settings';
import { TextingManagement } from '../../../containers/account/accountTabs/TextingManagement';
import { Users } from '../../../containers/account/accountTabs/Users';
import { ConditionalAccess } from '../../ConditionalAccess';
import { DisabledTabContent } from './presentation';
import { styleSheet } from './styles';

interface IProps extends IFullscreenModalComponentProps, IAdminUserSessionComponentProps, RouteComponentProps<any> {
	account: IAccount;
	accountOperations: AccountOperationsViewModel;
	adminUserSession?: AdminUserSessionContext;
	className?: string;
	disableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	enableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	isEditMode?: boolean;
	onCancelEdit?(): void;
	onHawksoftSetupModalRequestClose?(agencyId?: string, cancel?: boolean): void;
	onMakeMainContact?(contactId: string): void;
	scrollToBottomPortalId?: string;
	showHawksoftSetup?: boolean;
	styles?: StyleDeclarationValue[];
	grantTemporaryAccess?(): Promise<void>;
}

interface IState {
	selectedTabIndex: number;
	consumedUsers?: number;
	integrationsSearchQuery: string;
}

const TabValues = {
	User: 'Users',
	Imports: 'Imports',
	Texting: 'Texting',
	Tags: 'Tags',
	Emails: 'Emails',
	Social: 'Social',
	Automations: 'Automations',
	Integrations: 'Integrations',
	LeadParsers: 'Lead Parsers',
	Settings: 'Settings',
} as const;

type TabValues = (typeof TabValues)[keyof typeof TabValues];

class _AccountDetailsTabView extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			selectedTabIndex: 0,
			consumedUsers: -1,
			integrationsSearchQuery: '',
		};
	}

	public componentDidMount() {
		const { accountOperations } = this.props;
		if (accountOperations) {
			const userCountPromise = accountOperations.getUserCount();
			if (userCountPromise) {
				userCountPromise
					.then(count => this.setState({ consumedUsers: count }))
					.catch(() => {
						// Silently fail - will show just licensed count
						console.warn('Could not get the number of consumed users');
					});
			}
		}
	}

	public render() {
		const {
			account,
			accountOperations,
			adminUserSession,
			disableHawksoft,
			enableHawksoft,
			location,
			onHawksoftSetupModalRequestClose,
			onMakeMainContact,
			scrollToBottomPortalId,
			showHawksoftSetup,
			fullscreenModal,
		} = this.props;
		const search = getQueryStringParams(location.search);
		const selectedTab = search?.tab ?? TabValues.User;
		const impersonationContext: IImpersonationContext = { account };
		return (
			<Tabs.Root
				className={`tab-view ${css(styleSheet.tabViewContainer)}`}
				value={selectedTab}
				// @ts-ignore
				onValueChange={this.onSelectedTabChanged}
			>
				<div className='scroll-view tab-view-scroll-view'>
					<div className='scroll-view-content'>
						<div className='scroll-view-header'>
							<div className='scroll-view-header-content'>
								<Tabs.List className={`tab-view-tabbar tab-view-tabbar-tabs ${css(styleSheet.tabList)}`}>
									<Tabs.Trigger
										value={TabValues.User}
										className={`tab-view-tabbar-item ${selectedTab === TabValues.User ? `tabbar-item-selected` : ''}`}
									>
										{this.getUsersTabHeader()}
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Imports}
										className={`tab-view-tabbar-item ${
											selectedTab === TabValues.Imports ? `tabbar-item-selected` : ''
										}`}
									>
										Imports
									</Tabs.Trigger>
									{account?.features?.texting?.enabled ? (
										<Tabs.Trigger
											value={TabValues.Texting}
											className={`tab-view-tabbar-item ${
												selectedTab === TabValues.Texting ? `tabbar-item-selected` : ''
											}`}
										>
											Texting
										</Tabs.Trigger>
									) : null}
									<Tabs.Trigger
										value={TabValues.Tags}
										className={`tab-view-tabbar-item ${selectedTab === TabValues.Tags ? `tabbar-item-selected` : ''}`}
									>
										Tags
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Emails}
										className={`tab-view-tabbar-item ${selectedTab === TabValues.Emails ? `tabbar-item-selected` : ''}`}
									>
										Emails
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Social}
										className={`tab-view-tabbar-item ${selectedTab === TabValues.Social ? `tabbar-item-selected` : ''}`}
									>
										Social
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Automations}
										className={`tab-view-tabbar-item ${
											selectedTab === TabValues.Automations ? `tabbar-item-selected` : ''
										}`}
									>
										Automations
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Integrations}
										className={`tab-view-tabbar-item ${
											selectedTab === TabValues.Integrations ? `tabbar-item-selected` : ''
										}`}
									>
										Integrations
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.LeadParsers}
										className={`tab-view-tabbar-item ${
											selectedTab === TabValues.LeadParsers ? `tabbar-item-selected` : ''
										}`}
									>
										Lead Parsers
									</Tabs.Trigger>
									<Tabs.Trigger
										value={TabValues.Settings}
										className={`tab-view-tabbar-item ${
											selectedTab === TabValues.Settings ? `tabbar-item-selected` : ''
										}`}
									>
										Settings
									</Tabs.Trigger>
								</Tabs.List>
								<Tabs.Content value={TabValues.User} className='scroll-view-items'>
									<Users
										account={account}
										grantTemporaryAccess={this.props.grantTemporaryAccess}
										scrollToBottomPortalId={scrollToBottomPortalId}
										onMakeMainContact={onMakeMainContact}
									/>
								</Tabs.Content>
								<Tabs.Content value={TabValues.Imports} className='scroll-view-items'>
									<Imports accountVM={accountOperations} />
								</Tabs.Content>

								<Tabs.Content value={TabValues.Texting} className='scroll-view-items'>
									<TextingManagement account={account} />
								</Tabs.Content>

								<Tabs.Content value={TabValues.Tags} className={css(styleSheet.noScrollViewContainer)}>
									<TagsContainer isAdminContext={true} impersonationContext={impersonationContext} />
								</Tabs.Content>

								<Tabs.Content value={TabValues.Emails} className='scroll-view-items'>
									<ConditionalAccess
										elseChildren={this.onRenderNotAuthorized()}
										or={AdminPermission.ReadCampaigns}
										permission={AdminPermission.CreateCampaigns}
									>
										<GroupEmails
											accountId={accountOperations.account.id}
											onScheduleEmailsForClientClicked={() => {
												const locationState: ILocationState<AccountOperationsViewModel, any> = {
													viewModel: accountOperations,
												};

												fullscreenModal.history.push({
													pathname: '/email/bulk-email-approval',
													state: locationState,
												});
											}}
											onContentCalendarClicked={() => {
												const { origin, pathname } = window.location;
												window?.open(
													`${origin}${pathname}#/accounts/${accountOperations?.account?.id}/content-calendar`,
													'_blank'
												);
											}}
											onAccountViewBulkEmailAttempts={() => {
												this.props.history.push(
													`/email/account-bulk-historical-report/${accountOperations.account.id}`
												);
											}}
											onCampaignViewBulkEmailAttempts={campaign => {
												fullscreenModal.history.push(
													`/email/campaign-bulk-historical-report/${accountOperations.account.id}/${campaign.id}`
												);
											}}
											scrollToBottomPortalId={scrollToBottomPortalId}
										/>
									</ConditionalAccess>
								</Tabs.Content>
								<Tabs.Content value={TabValues.Social} className='scroll-view-items'>
									<ConditionalAccess
										elseChildren={this.onRenderNotAuthorized()}
										or={AdminPermission.ReadCampaigns}
										permission={AdminPermission.CreateCampaigns}
									>
										<AdminSocialMediaReportingTable
											impersonationContext={{ account }}
											scrollToBottomPortalId={scrollToBottomPortalId}
										/>
									</ConditionalAccess>
								</Tabs.Content>
								<Tabs.Content value={TabValues.Automations} className='scroll-view-items'>
									<ConditionalAccess
										elseChildren={this.onRenderNotAuthorized()}
										permission={AdminPermission.CreateAutomationTemplateOnBehalf}
									>
										{account?.features?.automation?.enabled ? (
											<AccountAutomationsTab account={account} />
										) : (
											<DisabledTabContent>Automations are disabled for this account.</DisabledTabContent>
										)}
									</ConditionalAccess>
								</Tabs.Content>
								<Tabs.Content value={TabValues.Integrations} className='scroll-view-items'>
									<Input
										styles={[styleSheet.integrationsSearchInput]}
										placeholder='Search integrations...'
										type='text'
										value={this.state.integrationsSearchQuery}
										onChange={e => this.setState({ integrationsSearchQuery: e.target.value })}
									/>
									<ExternalIntegrations
										impersonationContext={{ account }}
										searchQuery={this.state.integrationsSearchQuery}
									/>
									<AdminIntegrations
										accountVM={accountOperations}
										adminUserSession={adminUserSession}
										disableHawksoft={disableHawksoft}
										enableHawksoft={enableHawksoft}
										onHawksoftSetupModalRequestClose={onHawksoftSetupModalRequestClose}
										searchQuery={this.state.integrationsSearchQuery}
										showHawksoftSetup={showHawksoftSetup}
									/>
								</Tabs.Content>
								<Tabs.Content value={TabValues.LeadParsers} className='scroll-view-items'>
									<AdminLeadParsers accountVM={accountOperations} adminUserSession={adminUserSession} />
								</Tabs.Content>
								<Tabs.Content value={TabValues.Settings} className='scroll-view-items'>
									<Settings scrollToBottomPortalId={scrollToBottomPortalId} />
								</Tabs.Content>
							</div>
						</div>
					</div>
				</div>
			</Tabs.Root>
		);
	}

	private onRenderNotAuthorized = () => {
		return (
			<div className={css(styleSheet.unauthorized)}>
				<div>Sorry, you&apos;re not authorized</div>
				<div style={{ fontSize: 50 }}>😿</div>
			</div>
		);
	};

	private getUsersTabHeader = () => {
		const { account } = this.props;
		const { consumedUsers } = this.state;
		const totalLicenses = account.planDetails.users;

		if (consumedUsers > -1) {
			return `Users\u00A0(${consumedUsers}/${totalLicenses})`;
		}
		return `Users\u00A0(${totalLicenses})`;
	};

	private onSelectedTabChanged = (selectedTab: (typeof TabValues)[keyof typeof TabValues]) => {
		this.props.onCancelEdit();
		this.props.history.push({
			pathname: this.props.location.pathname,
			search: stringify({ tab: selectedTab }),
		});
	};
}

const AccountDetailsTabViewAsObserver = observer(_AccountDetailsTabView);
const AccountDetailsTabViewWithRouter = withRouter(AccountDetailsTabViewAsObserver);
export const AccountDetailsTabView = inject(
	FullScreenModalViewModelKey,
	UserSessionViewModelKey
)(AccountDetailsTabViewWithRouter);
